import { API_BASE_URL } from "@/utils/getApiBase";
import { request } from "../request";

/**
 * Parameter validation utilities for ApiStatisticsV1
 */
class ApiStatisticsValidator {
  static validateFrame(frame: number): boolean {
    return frame >= 1 && frame <= 8760;
  }

  static validateDateRange(startDate: string, endDate: string): boolean {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const maxRange = 365 * 24 * 60 * 60 * 1000; // 365 days in ms

    return start < end && (end.getTime() - start.getTime()) <= maxRange;
  }

  static validateGranularity(granularity: string, frame?: number): boolean {
    const validGranularities = ['auto', '1min', 'hourly', 'daily', 'monthly'];
    if (!validGranularities.includes(granularity)) return false;

    // 1min granularity not supported for large time frames
    if (granularity === '1min' && frame && frame > 168) return false;

    return true;
  }

  static validateModelName(model: string): boolean {
    return /^[a-zA-Z0-9_-]+$/.test(model);
  }

  static validateProviderName(provider: string): boolean {
    return /^[a-zA-Z0-9_-]+$/.test(provider);
  }

  static validateUUID(uuid: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  }
}

/**
 * Parameter sanitization utilities
 */
class ApiStatisticsParams {
  static sanitize(params: Partial<Statistics.ApiStatisticsQueryParams>): Statistics.ApiStatisticsQueryParams {
    const sanitized: Statistics.ApiStatisticsQueryParams = {};

    // Sanitize frame
    if (params.frame !== undefined) {
      sanitized.frame = Math.max(1, Math.min(8760, Math.floor(params.frame)));
    }

    // Sanitize strings
    if (params.model) {
      sanitized.model = params.model.replace(/[^a-zA-Z0-9_-]/g, '').substring(0, 100);
    }

    if (params.provider) {
      sanitized.provider = params.provider.replace(/[^a-zA-Z0-9_-]/g, '').substring(0, 50);
    }

    // Copy other valid parameters
    const validKeys: (keyof Statistics.ApiStatisticsQueryParams)[] = [
      'start_date', 'end_date', 'granularity', 'api_key_id',
      'response_format', 'include_model_usage', 'include_provider_usage',
      'include_token_details'
    ];

    validKeys.forEach(key => {
      if (params[key] !== undefined) {
        sanitized[key] = params[key] as any;
      }
    });

    return sanitized;
  }
}

/**
 * Fetches statistics data using the flexible ApiStatisticsV1 endpoint
 * Supports various time ranges, granularities, and filtering options
 * 
 * @param params - Query parameters for the statistics API
 * @returns Promise resolving to statistics response data
 */
export function fetchStatistics(params: Partial<Statistics.ApiStatisticsQueryParams> = {}) {
  // Set default parameters
  const defaultParams: Statistics.ApiStatisticsQueryParams = {
    frame: 24,
    granularity: 'hourly',
    response_format: 'combined',
    include_model_usage: true,
    include_provider_usage: true,
    include_token_details: true
  };

  // Merge with provided params and sanitize
  const mergedParams = { ...defaultParams, ...params };
  const sanitizedParams = ApiStatisticsParams.sanitize(mergedParams);

  // Validate critical parameters
  if (sanitizedParams.frame && !ApiStatisticsValidator.validateFrame(sanitizedParams.frame)) {
    throw new Error(`Invalid frame parameter: ${sanitizedParams.frame}. Must be between 1 and 8760.`);
  }

  if (sanitizedParams.start_date && sanitizedParams.end_date) {
    if (!ApiStatisticsValidator.validateDateRange(sanitizedParams.start_date, sanitizedParams.end_date)) {
      throw new Error('Invalid date range. End date must be after start date and within 365 days.');
    }
  }

  if (sanitizedParams.granularity && !ApiStatisticsValidator.validateGranularity(sanitizedParams.granularity, sanitizedParams.frame)) {
    throw new Error(`Invalid granularity: ${sanitizedParams.granularity}`);
  }

  if (sanitizedParams.model && !ApiStatisticsValidator.validateModelName(sanitizedParams.model)) {
    throw new Error(`Invalid model name: ${sanitizedParams.model}`);
  }

  if (sanitizedParams.provider && !ApiStatisticsValidator.validateProviderName(sanitizedParams.provider)) {
    throw new Error(`Invalid provider name: ${sanitizedParams.provider}`);
  }

  if (sanitizedParams.api_key_id && !ApiStatisticsValidator.validateUUID(sanitizedParams.api_key_id)) {
    throw new Error(`Invalid API key ID format: ${sanitizedParams.api_key_id}`);
  }

  // Build query string
  const queryString = new URLSearchParams(
    Object.entries(sanitizedParams)
      .filter(([_, value]) => value !== undefined)
      .map(([key, value]) => [key, String(value)])
  ).toString();

  return request<Statistics.ApiStatisticsResponse>({
    url: `${API_BASE_URL}/api/data/platform/statistics/v1/?${queryString}`,
    method: "get"
  });
}

// Export validation utilities for potential use in components
export { ApiStatisticsValidator, ApiStatisticsParams };